// domains/Grid/types/grid.types.ts
import { Post } from "@/core/types/postTypes";

// Grid item for planning grids
export interface GridItem {
  id: string;
  position: number;
  post: Post | null;
  isEmpty?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export type FeedbackType = "like" | "dislike";

export interface PostFeedback {
  id: number;
  postId: string;
  gridId: number;
  clientId: string;
  feedbackType: FeedbackType;
  comment: string | null;
  createdByEmail: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackSummary {
  totalFeedback: number;
  likes: number;
  dislikes: number;
  feedbackPercentage: number;
}

export interface GridData {
  id: string | number;
  name: string;
  description?: string;
  clientId: string;
  // NO grid_type - all grids are planning grids
  isActive: boolean;
  status: "draft" | "active" | "archived";
  postCount: number;
  createdAt: string | null;
  updatedAt: string | null;
  gridItems: GridItem[];
}

// Utility functions
export const createEmptyGrid = (size: number = 12): GridItem[] => {
  return Array.from({ length: size }, (_, index) => ({
    id: `empty-${index}`,
    position: index,
    post: null,
    isEmpty: true,
  }));
};

export const createEmptyGridItem = (position: number): GridItem => ({
  id: `empty-${position}`,
  position,
  post: null,
  isEmpty: true,
});

export const normalizeToSlots = (
  items: GridItem[],
  size: number = 12,
): GridItem[] => {
  const slots: GridItem[] = [];
  for (let i = 0; i < size; i++) {
    const existing = items.find((item) => item.position === i);
    slots.push(existing ?? createEmptyGridItem(i));
  }
  return slots;
};

export const isEmptyGridSlot = (item: GridItem | null | undefined): boolean => {
  return !item || !item.post || item.isEmpty === true;
};

export const hasValidPost = (item: GridItem | null | undefined): boolean => {
  return Boolean(item?.post && !item?.isEmpty);
};

export const canPostBeInGrid = (post: Post | null | undefined): boolean => {
  if (!post) return false;

  return (
    post.canBeInGrid &&
    (post.postType === "standard" || post.postType === "reel") &&
    post.mediaItems.length > 0
  );
};

export const getGridItemByPosition = (
  grid: GridItem[],
  position: number,
): GridItem | undefined => {
  return grid.find((item) => item.position === position);
};

export const getGridItemByPostId = (
  grid: GridItem[],
  postId: string,
): GridItem | undefined => {
  return grid.find((item) => item.post?.id === postId);
};

export const updateGridItemAtPosition = (
  grid: GridItem[],
  position: number,
  updates: Partial<GridItem>,
): GridItem[] => {
  return grid.map((item) =>
    item.position === position
      ? { ...item, ...updates, updatedAt: new Date().toISOString() }
      : item,
  );
};

export const swapGridItems = (
  grid: GridItem[],
  sourcePosition: number,
  targetPosition: number,
): GridItem[] => {
  const newGrid = [...grid];
  const sourceIndex = newGrid.findIndex(
    (item) => item.position === sourcePosition,
  );
  const targetIndex = newGrid.findIndex(
    (item) => item.position === targetPosition,
  );

  if (sourceIndex === -1 || targetIndex === -1) return grid;

  // Swap the posts but keep positions
  const sourcePost = newGrid[sourceIndex].post;
  const targetPost = newGrid[targetIndex].post;

  newGrid[sourceIndex] = {
    ...newGrid[sourceIndex],
    post: targetPost,
    isEmpty: !targetPost,
    updatedAt: new Date().toISOString(),
  };

  newGrid[targetIndex] = {
    ...newGrid[targetIndex],
    post: sourcePost,
    isEmpty: !sourcePost,
    updatedAt: new Date().toISOString(),
  };

  return newGrid;
};
